import type { GameModule, PlayerId } from '@games/core'
import type { Color, FogChessState, FogChessView, Move } from './types'
import { computeHidden, computeVisible } from './visibility'
import { applyMoveToState, createInitialState, isLegalMove } from './state'
import { hashState } from './hash'

export { COLS, ROWS, cellKey } from './types'
export type {
  Board,
  BoardMask,
  BoardPresentation,
  Color,
  FogChessPlayerView,
  FogChessSpectatorView,
  FogChessState,
  FogChessView,
  Move,
  Piece,
  PieceType,
  SpectateMode,
} from './types'

export { applyMove, createInitialBoard, emptyBoard, genMoves, inPalace } from './rules'
export type { LegalTarget } from './rules'

export { computeHidden, computeVisible, invertKeys } from './visibility'

export { applyMoveToState, cloneBoard, createInitialState, isLegalMove } from './state'

export { DEFAULT_METRICS, boardSize, cellCenter, cellFromPoint, fitCell, flipRC } from './geometry'
export type { BoardMetrics } from './geometry'

export { hashState } from './hash'

export { LABELS } from './labels'

/** 聊天快捷台词（游戏内容，由 registry 注册后交给通用层） */
export { FOG_CHESS_CHAT_TAUNTS } from './chat'

const other = (color: Color): Color => (color === 'r' ? 'b' : 'r')

/** 房间层用玩家 id 驱动游戏；本变体里玩家 id 就是颜色 `'r'` / `'b'`。 */
const asColor = (playerId: PlayerId): Color => (playerId === 'b' ? 'b' : 'r')

/** 战争迷雾象棋的游戏模块。 */
export const fogChess: GameModule<FogChessState, Move, FogChessView> = {
  id: 'fog-chess',
  name: '战争迷雾象棋',
  description: '规则和普通象棋一样，但只能看到己方棋子能走到的格子，其余都是战争迷雾；吃掉对方将/帅即分出胜负。',
  minPlayers: 2,
  maxPlayers: 2,

  createInitialState,

  isLegal(state, move, playerId) {
    return isLegalMove(state, move, asColor(playerId))
  },

  apply(state, move, playerId) {
    if (!isLegalMove(state, move, asColor(playerId))) return state
    return applyMoveToState(state, move)
  },

  viewFor(state, viewer) {
    // 观众：上帝视角（完整棋盘）+ 双方各自看不到的格子，供视图叠两层遮罩（D1/D2）
    if (viewer.kind === 'spectator') {
      return {
        kind: 'spectator',
        board: state.board,
        turn: state.turn,
        winner: state.winner,
        lastMove: state.lastMove,
        hiddenFromRed: computeHidden(state.board, 'r'),
        hiddenFromBlack: computeHidden(state.board, 'b'),
        disabled: true,
      }
    }

    const self = asColor(viewer.playerId)
    return {
      kind: 'player',
      self,
      flip: self === 'b',
      turn: state.turn,
      winner: state.winner,
      board: state.board,
      visible: [...computeVisible(state.board, self)],
      lastMove: state.lastMove,
      capturedMine: state.captured[self],
      capturedOpp: state.captured[other(self)],
      disabled: state.winner !== null || state.turn !== self,
    }
  },

  outcome(state) {
    if (state.winner === null) return null
    return { winner: state.winner, reason: '将/帅被吃' }
  },

  hash: hashState,
}